// @ts-nocheck
import {
  onConcordConfigLoaded,
  onConcordLoaded,
  onConcordReady,
  onPageReady,
} from './events';
import Events from './constants/events';
import {
  blockTrackers,
  addImpliedConsentEvents,
  addQueryParams,
  applyConsentManagement,
  checkDomain,
  loadPlugins,
  refreshData,
  refreshSession,
} from './handlers';
import {
  ComplianceProxy,
  ConsentProxy,
  PluginProxy,
  ProjectProxy,
  ProfileProxy,
} from './proxies';
import store from './store';
import { handleError } from './utilities';
import { version } from '../package.json';
import { coverBlockedIframesAndImages } from './lib/tracking/detectBlockedElements';

/**
 * The window.concord object is a proxy that is used to interact with the
 * Concord site client. It is the main entry point for the client and is
 * responsible for initializing the store, blocking trackers, applying consent
 * management, refreshing the session, refreshing the data, loading plugins, and
 * adding implied consent events.
 *
 * @remarks
 *   The proxy provides access to various functionalities and state management
 *   capabilities of the Concord client.
 * @type Proxy
 * @property resetSession - Resets the session and refreshes the data
 * @property onPageReady - Called when the page is ready
 * @property onConcordConfigLoaded - Called when the Concord config is loaded
 * @property onConcordLoaded - Called when Concord is loaded
 * @property onConcordReady - Called when Concord is ready (after config load,
 *   session refresh, data refresh, plugin load, and consent events setup)
 * @property version - The version of the Concord site client
 * @property enabledReleaseStages - The release stages enabled for tracking
 * @property env - The deployment environment
 * @property events - Available events for listening
 * @property compliance - The compliance proxy
 * @property consent - The consent proxy
 * @property project - The project proxy
 * @property profile - The profile proxy
 * @property plugin - The plugin proxy
 * @property config - The config object
 * @property ready - Whether Concord is ready
 * @property loaded - Whether Concord is loaded
 * @property contextId - The context ID
 * @property set - Initializes store and sets config on window.concord
 * @property get - Retrieves config, ready, loaded, or contextId properties
 */

type Callback = () => void;

type Proxy = {
  resetSession: () => void;
  onPageReady: (callback: Callback) => void;
  onConcordConfigLoaded: (callback: Callback) => void;
  onConcordLoaded: (callback: Callback) => void;
  onConcordReady: (callback: Callback) => void;
  version: string;
  enabledReleaseStages: string[];
  env: string;
  events: {
    Loaded: string;
    LoadedConfig: string;
    Ready: string;
    Unavailable: string;
  };
  compliance: object;
  consent: object;
  project: object;
  profile: object;
  plugin: object;
  config: object;
  ready: boolean;
  loaded: boolean;
  contextId: string;
  set: (obj: object, prop: string, value: string) => boolean;
  get: (obj: object, prop: string) => string;
};

declare global {
  interface Window {
    concord: Proxy | undefined;
  }
}

const DEPLOY_ENV = import.meta.env.MODE || 'prod';
const VERSION = version || 'unknown';

function createDomainSet(domains: string[] = []) {
  const domainSet = new Set<string>();
  // Add localhost by default
  domainSet.add('localhost');

  domains.forEach((domain) => {
    domainSet.add(domain);
    // Also add the non-www version if domain starts with www.
    if (domain.startsWith('www.')) {
      domainSet.add(domain.slice(4));
    }
  });
  return domainSet;
}

export function initialize() {
  /* eslint-disable-next-line */
  console.log(
    `Concord site client loaded. v${VERSION}${
      DEPLOY_ENV === 'prod' ? '' : ` ${DEPLOY_ENV}`
    }.`,
  );

  window.concord = new Proxy(
    {
      resetSession: () => {
        store.app.clearSession();
        store.app.refreshSession().then(() => store.refreshData());
      },
      onPageReady,
      onConcordConfigLoaded,
      onConcordLoaded,
      onConcordReady,
      version: VERSION,
      enabledReleaseStages: ['prod', 'stage'],
      env: DEPLOY_ENV,
      events: {
        Loaded: Events.Loaded,
        LoadedConfig: Events.LoadedConfig,
        Ready: Events.Ready,
        Unavailable: Events.Unavailable,
      },
      compliance: ComplianceProxy,
      consent: ConsentProxy,
      project: ProjectProxy,
      profile: ProfileProxy,
      plugin: PluginProxy,
      config: {},
      ready: false,
      loaded: false,
      contextId: '',
      set: () => false,
      get: () => '',
    },
    {
      get: (obj, prop) => {
        if (prop === 'config') {
          return Object.fromEntries(Object.entries(store.config));
        }
        if (prop === 'ready') {
          return store.ready;
        }
        if (prop === 'loaded') {
          return store.loaded;
        }
        if (prop === 'contextId') {
          return store.app.getContextId();
        }
        return Reflect.get(obj, prop);
      },
      /**
       * Initializes the store and sets the config property on the
       * window.concord object. This is the main entry point for the Concord
       * site client. It will initialize the store, block trackers, apply
       * consent management, refresh the session, refresh the data, load
       * plugins, and add implied consent events.
       *
       * @param {unknown} obj The window.concord object that is being proxied
       * @param {unknown} prop The property attempting to be set
       * @param {unknown} value The value that is attempting to be set
       * @returns
       */
      set: (obj: unknown, prop: unknown, value: unknown) => {
        if (prop === 'config') {
          // Create domains Set first
          const domainsSet = createDomainSet(value?.domains);

          if (
            !checkDomain(domainsSet) ||
            !value ||
            !value?.trackers ||
            !value?.regionTemplate ||
            !value?.regionTemplate?.id
          ) {
            window.concord = undefined;
            return true;
          }

          store.config.setConfig(value);
          store.domainsSet = domainsSet;
          store.setKnownTrackers(value.trackers || []);
          store.setConsentTypes(value.consentTypes || []);
          store.gcmMode =
            value.regionTemplate.settings.consent.gcm || 'disabled';
          const gcmEnabled =
            store.gcmMode === 'basic' || store.gcmMode === 'advanced';

          const defaultExpiration = Math.floor(12 * 30.44 * 24 * 60 * 60); // 12 months in seconds
          const expirationSetting =
            value.regionTemplate.settings.consent.consentDuration;
          let regionExpiration;

          if (
            typeof expirationSetting === 'number' &&
            expirationSetting > 0 &&
            expirationSetting <= 24
          ) {
            regionExpiration = Math.floor(
              expirationSetting * 30.44 * 24 * 60 * 60,
            ); // Convert months to seconds
          } else {
            regionExpiration = defaultExpiration; // Use default expiration
          }

          store.consentState.setConsent({
            regionId: value.regionTemplate.id,
            regionExpiration,
            gcmEnabled,
          });

          store.loaded = true;
          window.dispatchEvent(new CustomEvent(Events.LoadedConfig));

          /* Run initial blocking */
          blockTrackers(value);

          store
            .initialize()
            .catch(handleError)
            .finally(() => {
              refreshSession();
              /* Run remaining processing after the event loop */
              setTimeout(() => {
                refreshData();
                coverBlockedIframesAndImages();
                addImpliedConsentEvents();
                addQueryParams();
                // Start the async process of loading plugins
                loadPlugins();
                applyConsentManagement();
              }, 0);
            });
          return true;
        }
        return false;
      },
    },
  );

  window.dispatchEvent(new CustomEvent(Events.Loaded));
}
